import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBatchById } from "../../../api/BatchApi";

export default function BatchViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBatch();
  }, []);

  const loadBatch = async () => {
    try {
      const res = await getBatchById(id);
      let parsedCompliance = [];
      try {
        parsedCompliance = JSON.parse(res.data.complianceJson || "[]");
      } catch {
        parsedCompliance = [];
      }
      setBatch({
        ...res.data,
        complianceList: parsedCompliance
      });
    } catch (err) {
      console.error("Failed to load batch");
    } finally {
      setLoading(false);
    }
  };

  const openFile = (url) => url && window.open(url, "_blank", "noopener,noreferrer");

  if (loading) return (
    <div className="container py-5 text-center">
      <div className="spinner-border text-primary shadow-sm" role="status"></div>
      <p className="mt-2 fw-medium text-muted">Securing batch data...</p>
    </div>
  );

  if (!batch) return (
    <div className="container py-5 text-center">
      <div className="alert alert-warning border-0 shadow-sm">Record not found</div>
      <button className="btn btn-primary px-4 rounded-pill" onClick={() => navigate(-1)}>Go Back</button>
    </div>
  );

 const shouldShowTC =
  batch.batchType === "ORGANIC";

const shouldShowHealthCert =
  batch.country === "EU";

const reportFields = [

  ...(shouldShowTC
    ? [{ id: "TC", label: "Transaction Cert (TC)" }]
    : []),

  ...(shouldShowHealthCert
    ? [{ id: "healthCert", label: "Health Certificate" }]
    : []),

  { id: "pesticides", label: "Pesticides" },
  { id: "microbiology", label: "Microbiology" },
  { id: "heavyMetal", label: "Heavy Metal" },
  { id: "ETO", label: "ETO" },
  { id: "efficacy", label: "Efficacy" },
  { id: "internalCoA", label: "Internal CoA" },
  { id: "MSDS", label: "MSDS Sheet" }
];

const handleShareQR = async () => {
  try {
    if (!batch?.qrCodeUrl) return;

    // fetch image
    const res = await fetch(batch.qrCodeUrl);
    const blob = await res.blob();
    const file = new File([blob], `${batch.batchNumber || "batch"}-qr.png`, {
      type: blob.type || "image/png",
    });

    // mobile browsers that support file share
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        title: `Batch ${batch.batchNumber}`,
        text: "Scan this QR",
        files: [file],
      });
      return;
    }

    // fallback 1: try sharing URL (desktop browsers that support share)
    if (navigator.share) {
      await navigator.share({
        title: `Batch ${batch.batchNumber}`,
        text: "Scan this QR",
        url: batch.qrCodeUrl,
      });
      return;
    }

    // fallback 2: copy + open image
    await navigator.clipboard.writeText(batch.qrCodeUrl);
    window.open(batch.qrCodeUrl, "_blank");
    alert("QR opened and link copied for sharing");
  } catch (err) {
    console.error("Share failed:", err);
    alert("Sharing not supported on this browser");
  }
};




  return (
    <div className="container-fluid py-4 px-md-5 bg-light min-vh-100">
      {/* HEADER SECTION */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb small mb-1">
              <li className="breadcrumb-item text-primary cursor-pointer" onClick={() => navigate(-1)}>Batches</li>
              <li className="breadcrumb-item active">View Details</li>
            </ol>
          </nav>
          <h4 className="fw-bold text-dark mb-0">Batch: {batch.batchNumber}</h4>
        </div>
        <button className="btn btn-white bg-white border shadow-sm px-3 fw-bold small" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-2"></i>Back to List
        </button>
      </div>

      <div className="row g-4">
        {/* LEFT COLUMN: BASIC INFO & COMPLIANCE */}
        <div className="col-lg-8">
          
          {/* BASIC INFORMATION CARD */}
          <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
            <div className="card-header bg-white border-0 pt-4 px-4">
              <h6 className="fw-bold text-primary mb-0 text-uppercase small" style={{ letterSpacing: '1px' }}>
                <i className="bi bi-info-circle me-2"></i>Batch Details
              </h6>
            </div>
            <div className="card-body p-4 pt-3">
              <div className="row g-3">
                <Field label="Product Code" value={batch.productCode} icon="upc-scan" />
                <Field label="Product Name" value={batch.productName} icon="box-seam" />
                <Field label="Batch Number" value={batch.batchNumber} icon="hash" highlight />
                <Field label="Manufacturing Date" value={batch.manufacturingDate} icon="calendar-date" />
                <Field label="Expiry Date" value={batch.expiryDate} icon="calendar-x" />
                <Field label="Batch Size" value={`${batch.batchSizeKgs || "-"} KGS`} />
                <Field label="3rd Party Sampling" value={batch.thirdPartySampling ? "Yes" : "No"} />
                <Field label="Destination Country" value={batch.country} icon="globe-americas" />
                <Field label="Batch Status" value={batch.batchType} icon="tags" />
                <Field label="Report Status" value={batch.reportStatus} icon="file-earmark-check" statusBadge />
              </div>
            </div>
          </div>

          {/* COMPLIANCE CHECKLIST CARD */}
          <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
            <div className="card-header bg-white border-0 pt-4 px-4">
              <h6 className="fw-bold text-success mb-0 text-uppercase small" style={{ letterSpacing: '1px' }}>
                <i className="bi bi-shield-check me-2"></i>Batch Compliance Status
              </h6>
            </div>
            <div className="card-body p-4 pt-3">
              <div className="table-responsive">
                <table className="table table-hover align-middle custom-table mb-0">
                  <thead className="bg-light">
                    <tr className="text-muted small">
                      <th className="border-0 rounded-start">REGULATION</th>
                      <th className="border-0">STATUS</th>
                      <th className="border-0 rounded-end">REFERENCE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batch.complianceList.map((c, i) => (
                      <tr key={i}>
                        <td className="fw-medium text-dark">{c.item}</td>
                        <td>
                          <span className={`badge rounded-pill px-3 py-2 ${c.status === "YES" ? "bg-success-subtle text-success border border-success-subtle" : "bg-danger-subtle text-danger border border-danger-subtle"}`}>
                            <i className={`bi ${c.status === "YES" ? "bi-check-circle-fill" : "bi-x-circle-fill"} me-1`}></i>
                            {c.status}
                          </span>
                        </td>
                        <td className="text-muted small">{c.regulation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: REPORTS & QR */}
        <div className="col-lg-4">
          
          {/* REPORTS SECTION */}
          <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
            <div className="card-header bg-white border-0 pt-4 px-4">
              <h6 className="fw-bold text-dark mb-0 text-uppercase small" style={{ letterSpacing: '1px' }}>
                <i className="bi bi-file-earmark-pdf me-2 text-danger"></i>Batch Reports
              </h6>
            </div>
            <div className="card-body p-4 pt-3">
             <div className="vstack gap-2">
                {reportFields.map(r => {
                  const fileUrl = batch.reports?.[r.id];
                  const uploaded = !!fileUrl;

                  return (
                    <div
                      key={r.id}
                      className={`d-flex align-items-center justify-content-between p-3 rounded-3 border-start border-4 ${
                        uploaded
                          ? "bg-success-subtle border-success"
                          : "bg-light border-secondary"
                      }`}
                    >
                      <div className="d-flex align-items-center">
                        <i className={`bi ${uploaded ? "bi-file-check text-success" : "bi-file-x text-muted"} fs-5 me-3`}></i>

                        <div>
                          <div className="small fw-bold">{r.label}</div>
                          <div className="tiny-text text-muted">
                            {uploaded ? "Document available" : "Not uploaded"}
                          </div>
                        </div>
                      </div>

                      {uploaded && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openFile(fileUrl)}
                        >
                          View
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          </div>

          {/* QR CODE SECTION */}
          {batch.qrCodeUrl && (
            <div className="card shadow-sm border-0 rounded-4 text-center overflow-hidden">
              <div className="card-header bg-white border-0 pt-4">
                <h6 className="fw-bold text-dark mb-0 text-uppercase small" style={{ letterSpacing: '1px' }}>BATCH Traceability</h6>
              </div>
              <div className="card-body p-4">
                <div className="bg-white p-3 rounded-4 border border-light-subtle d-inline-block shadow-sm mb-3">
                  <img src={batch.qrCodeUrl} alt="QR" style={{ width: '100%', maxWidth: 180 }} className="img-fluid" />
                </div>
                <p className="text-muted tiny-text fw-bold mb-0">SCAN TO VERIFY AUTHENTICITY</p>
                <button
                  className="btn btn-primary btn-sm px-4 fw-bold"
                  onClick={handleShareQR}
                >
                  <i className="bi bi-share me-2"></i>
                  Share QR
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .tiny-text { font-size: 0.65rem; letter-spacing: 0.8px; }
        .custom-table th { font-size: 0.7rem; padding: 12px; }
        .custom-table td { padding: 14px 12px; font-size: 0.85rem; }
        .shadow-xs { box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
        .transition { transition: all 0.2s ease; }
        .hover-shadow:hover { background-color: #fff !important; box-shadow: 0 4px 12px rgba(0,0,0,0.08); transform: translateY(-2px); }
        .cursor-pointer { cursor: pointer; }
      `}</style>
    </div>
  );
}

function Field({ label, value, icon, highlight, statusBadge }) {
  return (
    <div className="col-md-6 col-xl-4">
      <div className={`p-3 rounded-3 h-100 ${highlight ? 'bg-primary bg-opacity-10 border border-primary-subtle' : 'bg-light border-light-subtle border'}`}>
        <div className="d-flex align-items-center mb-1">
          {icon && <i className={`bi bi-${icon} me-2 text-muted`} style={{ fontSize: '0.9rem' }}></i>}
          <div className="text-muted tiny-text fw-bold text-uppercase">{label}</div>
        </div>
        {statusBadge ? (
          <span className="badge bg-primary px-3 rounded-pill mt-1 fw-medium">{value || "-"}</span>
        ) : (
          <div className={`fw-bold ${highlight ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '0.9rem' }}>
            {value || "-"}
          </div>
        )}
      </div>
    </div>
  );
}